import { ColumnDef, flexRenderComponent } from '@tanstack/angular-table';
import { CasesHistory } from '../interfaces/cases-history.interface';
import { TableDateCellComponent } from '@core/components/table/table-date-cell/table-date-cell.component';
import { TableButtonCellComponent } from '@core/components/table/table-button-cell/table-button-cell.component';
import { CasesService } from '@features/cases/services/cases.service';
import { FairnessButtonComponent } from '@core/components/fairness-button/fairness-button.component';
import { ModalService } from '@features/modals/services/modal.service';
import { CostComponent } from '@core/components/cost/cost.component';

interface Props {
  casesService: CasesService;
  modalService: ModalService;
}

export const casesHistoryConfig = ({
  casesService,
  modalService,
}: Props): ColumnDef<CasesHistory>[] => [
  {
    accessorFn: (row) => row.id,
    id: 'id',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'ID',
      };
    },
    cell: (cell) => cell.getValue(),
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.value,
    id: 'value',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Amount',
      };
    },
    cell: (cell) => {
      const value = cell.getValue() as number;
      return flexRenderComponent(CostComponent, {
        inputs: {
          cost: value,
          balanceType: cell.row.original.balancetype,
          gap: 4,
          color: 'var(--yellow)',
        },
      });
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.outcome,
    id: 'outcome',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Payout',
      };
    },
    cell: (cell) => {
      const casesCost = cell.row.original.value;
      const casesProfit = cell.cell.getValue() as number;
      const getOutcomeColor = () => {
        if (casesProfit >= casesCost) {
          return 'var(--green)';
        } else if (casesProfit === 0) {
          return 'var(--red)';
        } else {
          return 'var(--yellow)';
        }
      };

      return flexRenderComponent(CostComponent, {
        inputs: {
          cost: casesProfit,
          balanceType: cell.row.original.balancetype,
          gap: 4,
          color: getOutcomeColor(),
        },
      });
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.case,
    id: 'details',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Details',
      };
    },
    cell: (cell) => {
      const { case: caseItem, skin } = cell.row.original;
      const currentCase = casesService
        .casesList()
        .find((c) => c.link === caseItem);
      if (!currentCase) {
        return '-';
      }

      return flexRenderComponent(TableButtonCellComponent, {
        inputs: {
          text: 'View Drops',
        },
        outputs: {
          handleClick: () => {
            modalService.openModal('CaseBattlesCaseSkinsModalComponent', {
              caseItem: {
                ...currentCase,
                skins: [
                  currentCase.skins.find((c) => c.name === skin) ?? null,
                ].filter((c) => c !== null),
              },
              balance: cell.row.original.balancetype,
            });
          },
        },
      });
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.case,
    id: 'case',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Case',
      };
    },
    cell: (cell) => {
      const { case: caseItem } = cell.row.original;
      const currentCase = casesService
        .casesList()
        .find((c) => c.link === caseItem);

      if (!currentCase) {
        return '-';
      }
      return flexRenderComponent(TableButtonCellComponent, {
        inputs: {
          text: 'View Case',
          color: 'var(--blue)',
          textColor: 'var(--white)',
        },
        outputs: {
          handleClick: () => {
            modalService.openModal('CaseBattlesCaseSkinsModalComponent', {
              caseItem: currentCase,
              balance: cell.row.original.balancetype,
            });
          },
        },
      });
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.time,
    id: 'time',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Date',
      };
    },
    cell: (cell) => {
      const value = cell.getValue() as number;
      return flexRenderComponent(TableDateCellComponent, {
        inputs: {
          value: value * 1000,
        },
      });
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.id,
    id: 'fairness',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Fairness',
      };
    },
    cell: (cell) => {
      return flexRenderComponent(FairnessButtonComponent, {
        inputs: {
          href: `provably-fair/caseopening/${cell.row.original.id}`,
        },
        outputs: {
          handleClick: () => {
            modalService.close();
          },
        },
      });
    },
    enableSorting: false,
  },
];
